import React, { useState, useEffect, useRef } from 'react';
import { FaMoneyBillWave, FaCreditCard, FaPaypal, FaCcStripe, FaCcVisa, FaUserAlt, FaHome, FaStore, FaWallet } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useToast } from '../context/ToastContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { API_CONFIG } from '../config/apiConfig';
import { 
  getPaymentMethods, 
  getUserAddresses, 
  createOrder,
  sendNotificationToStores,
  sendCustomerOrderEmail,
  getWalletBalance,
  getRazorpayKey,
  verifyRazorpayPurchase,
  getPayPalKey,
  getPaystackKey,
  getFlutterwaveKey
} from '../api/authApi';
import { 
  loadRazorpay,
  loadPaystack,
  loadFlutterwave,
  waitForGateway
} from '../utils/paymentGatewayLoader';

import OrderSuccessModal from '../components/OrderSuccessModal';
import { sendBrevoOrderEmail } from '../services/brevoEmailService';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const { user, token } = useAuth();
  const toast = useToast();
  
  // Step management
  const [currentStep] = useState(1); // 1: delivery, 2: address, 3: payment
  
  // Delivery options
  const [deliveryOption, setDeliveryOption] = useState('home'); // 'home' or 'store'
  
  // Addresses
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  
  // Store addresses
  
  // Payments
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [payMethodName, setPayMethodName] = useState('');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Order processing
  const [processing, setProcessing] = useState(false);
  const orderCreationInFlightRef = useRef(false);
  const processedOrderIdsRef = useRef(new Set());
  
  // Success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successOrderId] = useState(null);
  

  
  // Wallet
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWalletBalance, setUseWalletBalance] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingWallet, setLoadingWallet] = useState(false);
  
  // Form data for new address
  const [formData, setFormData] = useState({
    address: '',
    house: '',
    landmark: '',
    title: 'home',
    pincode: '',
    lat: '',
    lng: ''
  });
  
  // User info from auth context
  const userInfo = {
    id: user?.id || localStorage.getItem('uid') || null,
    email: user?.email || localStorage.getItem('userEmail') || 'guest@example.com',
    first_name: user?.first_name || localStorage.getItem('userFirstName') || 'Guest',
    last_name: user?.last_name || localStorage.getItem('userLastName') || 'User',
    mobile: user?.mobile || localStorage.getItem('userMobile') || '',
  };

  // Check if user is logged in
  const isLoggedIn = userInfo.id && userInfo.id !== 'null' && token;
  
  // Calculate totals
  const subtotal = cart.totalPrice || 0;
  const discount = cart.discount || 0;
  // eslint-disable-next-line no-unused-vars
  const deliveryCharge = cart.deliveryPrice || 0;
  // eslint-disable-next-line no-unused-vars
  const tax = 0;
  const total = cart.grandTotal || 0;
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission for new address
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Add address creation logic here
  };
  
  // Helper function to get item image
  const getItemImage = (item) => {
    if (item.cover && typeof item.cover === 'string' && item.cover.trim()) {
      return item.cover.startsWith('http') ? item.cover : `${API_CONFIG.IMAGE_URL}${item.cover}`;
    }
    return '/assets/img/product/default.png';
  };
  
  // Helper function to get item price
  const parsePrice = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const str = String(value).trim();
    if (str === '') return 0;
    const cleaned = str.replace(/[^0-9.-]+/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };
  
  const getItemPrice = (item) => {
    // For variation products with discount field, use discount as the final price
    if (item.variant !== null && item.variant !== undefined && item.variations && item.variations[0]?.items?.[item.variant]) {
      const variantData = item.variations[0].items[item.variant];
      const variantDiscount = parseFloat(variantData.discount) || 0;
      
      if (variantDiscount > 0) {
        // Use discount field as the final price
        return variantDiscount;
      }
      // No discount field, use variant price
      return parsePrice(variantData.price);
    }
    
    // For non-variation products, use sell_price or original_price
    const discountValue = parseFloat(item.discount) || 0;
    if (discountValue === 0) {
      return parsePrice(item.original_price);
    } else {
      return parsePrice(item.sell_price);
    }
  };
  
  const getItemTotal = (item) => {
    return (getItemPrice(item) * item.quantiy).toFixed(2);
  };
  
  // Calculate total amount for payment
  const calculateTotalAmount = () => {
    if (useWalletBalance && walletBalance > 0) {
      const remainingAmount = total - walletBalance;
      return remainingAmount > 0 ? remainingAmount : 0;
    }
    return total;
  };
  
  // Load payment methods when reaching step 3
  useEffect(() => {
    if (isLoggedIn && paymentMethods.length === 0) {
      loadPaymentMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  
  // Load addresses when component mounts (if logged in)
  useEffect(() => {
    if (isLoggedIn && addresses.length === 0) {
      loadAddresses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  
  // Load wallet balance when component mounts (if logged in)
  useEffect(() => {
    if (isLoggedIn) {
      loadWallet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  
  // Handle PayPal return from redirect
  useEffect(() => {
    const handlePayPalReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paypalStatus = urlParams.get('paypal');
      const paypalOrderId = urlParams.get('token') || localStorage.getItem('paypal_order_id');
      const payerId = urlParams.get('PayerID');
      
      // Check if user cancelled
      if (paypalStatus === 'cancel') {
        showMessage('info', 'PayPal payment was cancelled');
        localStorage.removeItem('paypal_order_id');
        localStorage.removeItem('paypal_cart_data');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // Check if payment was successful
      if ((paypalStatus === 'success' || payerId) && paypalOrderId) {
        setProcessing(true);
        
        try {
          // Load PayPal SDK to capture payment
          const keyResult = await getPayPalKey(token);
          if (keyResult.success) {
            await loadPayPalSDK(keyResult.key, 'USD');
            
            // Capture the payment
            const captureResponse = await window.paypal.request({
              method: 'post',
              path: `/v2/checkout/orders/${paypalOrderId}/capture`
            });
            
            // Restore cart data from localStorage
            const cartDataStr = localStorage.getItem('paypal_cart_data');
            if (cartDataStr) {
              const cartData = JSON.parse(cartDataStr);
              
              // Create order with PayPal transaction details
              await createOrderAfterPayment(JSON.stringify(captureResponse), cartData);
              
              // Clean up localStorage
              localStorage.removeItem('paypal_order_id');
              localStorage.removeItem('paypal_cart_data');
            } else {
              showMessage('error', 'Cart data not found. Please try again.');
              setProcessing(false);
            }
          }
        } catch (error) {
          // Error handling
          showMessage('error', 'Failed to process PayPal payment: ' + error.message);
          setProcessing(false);
        }
        
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };
    
    if (token) {
      handlePayPalReturn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  
  const loadAddresses = async () => {
    if (!token || !userInfo.id) return;
    setLoadingAddresses(true);
    try {
      const result = await getUserAddresses(userInfo.id, token);
      if (result.success && result.data) {
        setAddresses(result.data);
        // Auto-select first address
        if (result.data.length > 0 && !selectedAddressId) {
          setSelectedAddressId(result.data[0].id);
        }
      }
    } catch (error) {
      // Error handling
    } finally {
      setLoadingAddresses(false);
    }
  };
  
  const loadPaymentMethods = async () => {
    if (!token) {
      return;
    }
    setLoadingPayments(true);
    try {
      const result = await getPaymentMethods(token);
      if (result.success && Array.isArray(result.data) && result.data.length > 0) {
        let methods = result.data;
        
        // Always ensure PayPal is included (id: 3)
        const hasPayPal = methods.some(method => method.id === 3);
        if (!hasPayPal) {
          methods.push({
            id: 3,
            name: 'PayPal',
            currency_code: 'USD'
          });
        }
        
        setPaymentMethods(methods);
        // Auto-select COD if available
        const codMethod = methods.find(p => p.id === 1);
        if (codMethod) {
          setSelectedPaymentId(1);
          setPayMethodName(codMethod.name);
        }
      } else {
        // If no payment methods from API, still show PayPal
        const defaultMethods = [{
          id: 3,
          name: 'PayPal',
          currency_code: 'USD'
        }];
        setPaymentMethods(defaultMethods);
        showMessage('error', result.message || 'No payment methods found');
      }
    } catch (error) {
      // Error handling
      // Even on error, show PayPal as fallback
      const fallbackMethods = [{
        id: 3,
        name: 'PayPal',
        currency_code: 'USD'
      }];
      setPaymentMethods(fallbackMethods);
      showMessage('error', 'Failed to load payment methods');
    } finally {
      setLoadingPayments(false);
    }
  };
  
  const loadWallet = async () => {
    if (!token || !userInfo.id) return;
    setLoadingWallet(true);
    try {
      const result = await getWalletBalance(userInfo.id, token);
      if (result.success && result.data) {
        const balance = parseFloat(result.data.balance) || 0;
        setWalletBalance(balance);
      }
    } catch (error) {
      // Error handling
    } finally {
      setLoadingWallet(false);
    }
  };
  
  const handleWalletChange = (e) => {
    const checked = e.target.checked;
    
    setUseWalletBalance(checked);
    
    if (checked) {
      cart.setWalletDiscount(walletBalance);
    } else {
      cart.setWalletDiscount(0);
    }
    cart.calculate();
  };
  
  const showMessage = (type, text) => {
    if (type === 'success') {
      toast.showSuccess(text);
    } else if (type === 'error') {
      toast.showError(text);
    } else if (type === 'info') {
      toast.showInfo(text);
    } else {
      toast.showToast(text, type);
    }
  };
  
  const handlePaymentSelection = (method) => {
    setSelectedPaymentId(method.id);
    setPayMethodName(method.name);
    setCurrencyCode(method.currency_code || 'USD');
  };
  
  const handleProceedToPay = async () => {
    // Check if user is logged in
    if (!isLoggedIn || !token) {
      showMessage('error', 'Please login to place an order');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    
    if (!selectedPaymentId) {
      showMessage('error', 'Please select a payment method');
      return;
    }
    
    if (cart.cart.length === 0) {
      showMessage('error', 'Your cart is empty');
      return;
    }
    
    if (cart.totalPrice < cart.minOrderPrice) {
      showMessage('error', `Minimum order amount must be $${cart.minOrderPrice} or more`);
      return;
    }
    
    // Validate address for home delivery
    if (deliveryOption === 'home' && !selectedAddressId) {
      showMessage('error', 'Please select a delivery address');
      return;
    }
    
    // Route to payment handler
    if (selectedPaymentId === 1) {
      await handleCODPayment();
    } else if (selectedPaymentId === 3) {
      await handlePayPalPayment();
    } else if (selectedPaymentId === 5) {
      await handleRazorpayPayment();
    } else if (selectedPaymentId === 7) {
      await handlePaystackPayment();
    } else if (selectedPaymentId === 8) {
      await handleFlutterwavePayment();
    } else {
      showMessage('info', `${payMethodName} payment coming soon`);
    }
  };
  
  const handleRazorpayPayment = async () => {
    setProcessing(true);
    try {
      // Load Razorpay script
      showMessage('info', 'Loading Razorpay...');
      await loadRazorpay();
      await waitForGateway('Razorpay');
      
      // Get Razorpay key
      const keyResult = await getRazorpayKey(token);
      if (!keyResult.success || !keyResult.key) {
        showMessage('error', 'Failed to initialize Razorpay');
        setProcessing(false);
        return;
      }
      
      const options = {
        key: keyResult.key,
        amount: cart.grandTotal * 100, // Razorpay uses paise
        currency: currencyCode || 'USD',
        name: 'Tazza Tropics',
        description: `Order of $${cart.grandTotal}`,
        image: '/assets/img/logo/nav-log.png',
        handler: async (response) => {

          
          // Verify payment
          const verifyResult = await verifyRazorpayPurchase(response.razorpay_payment_id, token);
          if (verifyResult.success) {
            await createOrderAfterPayment(JSON.stringify(verifyResult.data));
          } else {
            showMessage('error', 'Payment verification failed');
            setProcessing(false);
          }
        },
        prefill: {
          name: `${userInfo.first_name} ${userInfo.last_name}`,
          email: userInfo.email,
          contact: userInfo.mobile || ''
        },
        theme: {
          color: '#f47878'
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        // Error handling
        showMessage('error', 'Payment failed. Please try again.');
        setProcessing(false);
      });
      
      rzp.open();
    } catch (error) {
      // Error handling
      showMessage('error', 'Failed to initialize payment');
      setProcessing(false);
    }
  };
  
  const handlePaystackPayment = async () => {
    setProcessing(true);
    try {
      // Load Paystack script
      showMessage('info', 'Loading Paystack...');
      await loadPaystack();
      await waitForGateway('PaystackPop');
      
      // Get Paystack key
      const keyResult = await getPaystackKey(token);
      if (!keyResult.success || !keyResult.key) {
        showMessage('error', 'Failed to initialize Paystack');
        setProcessing(false);
        return;
      }
      
      const handler = window.PaystackPop.setup({
        key: keyResult.key,
        email: userInfo.email,
        amount: cart.grandTotal * 100, // Paystack uses kobo
        currency: currencyCode || 'USD',
        ref: '' + Math.floor((Math.random() * 1000000000) + 1),
        callback: async (response) => {
          await createOrderAfterPayment(response.reference);
        },
        onClose: () => {
          showMessage('info', 'Payment cancelled');
          setProcessing(false);
        }
      });
      
      handler.openIframe();
    } catch (error) {
      // Error handling
      showMessage('error', 'Failed to initialize payment');
      setProcessing(false);
    }
  };
  
  const handleFlutterwavePayment = async () => {
    setProcessing(true);
    try {
      // Load Flutterwave script
      showMessage('info', 'Loading Flutterwave...');
      await loadFlutterwave();
      await waitForGateway('FlutterwaveCheckout');
      
      // Get Flutterwave key
      const keyResult = await getFlutterwaveKey(token);
      if (!keyResult.success || !keyResult.key) {
        showMessage('error', 'Failed to initialize Flutterwave');
        setProcessing(false);
        return;
      }
      
      window.FlutterwaveCheckout({
        public_key: keyResult.key,
        tx_ref: '' + Math.floor((Math.random() * 1000000000) + 1),
        amount: cart.grandTotal,
        currency: currencyCode || 'USD',
        payment_options: 'card, mobilemoneyghana, ussd',
        customer: {
          email: userInfo.email,
          phone_number: userInfo.mobile || '',
          name: `${userInfo.first_name} ${userInfo.last_name}`
        },
        callback: async (data) => {
          // Hide iframe
          const iframe = document.querySelector('iframe[name="checkout"]');
          if (iframe) {
            iframe.style.display = 'none';
          }
          await createOrderAfterPayment(JSON.stringify(data));
        },
        onclose: () => {
          showMessage('info', 'Payment cancelled');
          setProcessing(false);
        },
        customizations: {
          title: 'Tazza Tropics',
          description: 'Order Payment',
          logo: '/assets/img/logo/nav-log.png'
        }
      });
    } catch (error) {
      // Error handling
      showMessage('error', 'Failed to initialize payment');
      setProcessing(false);
    }
  };
  
  const createOrderAfterPayment = async (payKey, savedCartData = null) => {
    if (orderCreationInFlightRef.current) {
      console.warn('Order creation already in progress. Skipping duplicate submission.');
      return;
    }

    orderCreationInFlightRef.current = true;

    try {
      // Validate cart has items
      if (!cart.cart || cart.cart.length === 0) {
        throw new Error('Cart is empty. Cannot create order.');
      }
      
      // Validate user is logged in
      if (!userInfo.id || userInfo.id === 'null') {
        throw new Error('User not logged in. Cannot create order.');
      }
      
      // Use saved cart data if provided (for PayPal redirect return), otherwise use current state
      const cartData = savedCartData || {
        cartItems: cart.items,
        addressId: selectedAddressId,
        deliveryOption: deliveryOption,
        useWallet: useWalletBalance,
        paymentMethodId: selectedPaymentId,
        currencyCode: currencyCode
      };
      
      const storeIds = cart.getStoreIds();
      
      const orderStatusByStore = storeIds.map(id => ({ id, status: 'created' }));
      
      const orderNotes = [{
        status: 1,
        value: 'Order Created',
        time: moment().format('lll')
      }];
      
      const selectedAddress = cartData.deliveryOption === 'home' && addresses.length > 0 
        ? addresses.find(a => a.id === cartData.addressId)
        : null;
      
      // Enrich cart items with selected variation details so backend templates can access them
      const ordersWithVariants = (cart.cart || []).map((item) => {
        let selectedVariation = null;
        let parsedVariations = item.variations;
        
        // Check if product has variations (size === 1 means variant product)
        if ((item.size === '1' || item.size === 1) && item.variations) {
          try {
            // Parse variations if it's a string (from API it comes as JSON string)
            const variationsArray = typeof item.variations === 'string' 
              ? JSON.parse(item.variations) 
              : item.variations;
            
            parsedVariations = variationsArray;
            
            if (variationsArray && variationsArray.length > 0) {
              const variationGroup = variationsArray[0];
              const variantIndex = parseInt(item.variant) || 0;
              const selectedItem = variationGroup?.items?.[variantIndex];
              
              if (selectedItem) {
                selectedVariation = {
                  id: selectedItem.id ?? selectedItem.variant_id ?? null,
                  title: selectedItem.title ?? selectedItem.name ?? null,
                  price: parseFloat(selectedItem.price ?? selectedItem.sell_price ?? selectedItem.original_price ?? 0),
                  discount: parseFloat(selectedItem.discount ?? 0),
                  type: variationGroup.type ?? null,
                  group_title: variationGroup.title ?? null
                };
              }
            }
          } catch (e) {
            // Error handling
          }
        }
        
        return { ...item, variations: parsedVariations, selectedVariation };
      });

      console.log('📦 Orders being sent to backend:', ordersWithVariants.map(item => ({
        id: item.id,
        name: item.name,
        variant: item.variant,
        quantiy: item.quantiy,
        price: item.price,
        selectedVariation: item.selectedVariation
      })));

      const orderData = {
        uid: userInfo.id,
        store_id: storeIds.join(','),
        date_time: moment().format('YYYY-MM-DD HH:mm:ss'),
        paid_method: payMethodName,
        order_to: deliveryOption,
        orders: JSON.stringify(ordersWithVariants),
        notes: JSON.stringify(orderNotes),
        address: selectedAddress ? JSON.stringify(selectedAddress) : '',
        driver_id: '',
        total: cart.totalPrice,
        tax: 0,
        grand_total: cart.grandTotal,
        delivery_charge: cart.deliveryPrice,
        coupon_code: '',
        discount: cart.discount,
        pay_key: payKey,
        status: JSON.stringify(orderStatusByStore),
        assignee: '',
        extra: JSON.stringify(cart.userOrderTaxByStores || []),
        payStatus: 1, // Paid
        wallet_used: useWalletBalance ? 1 : 0,
        wallet_price: useWalletBalance ? cart.walletDiscount : 0
      };
      
      const result = await createOrder(orderData, token);
      
      if (!result) {
        throw new Error('No response from order creation API');
      }
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to create order');
      }
      
      if (result.success && result.data?.id) {
        const newOrderId = result.data.id;

        if (processedOrderIdsRef.current.has(newOrderId)) {
          console.warn(`Order ${newOrderId} was already processed. Skipping duplicate email dispatch.`);
          return;
        }

        processedOrderIdsRef.current.add(newOrderId);
        
        // Clear cart
        cart.clearCart();
        
        try {
          // Send store notifications
          try {
            await sendNotificationToStores(storeIds, newOrderId, token);
          } catch (notifError) {
            // Error handling
          }

          // Send order confirmation emails via API (same as Pure Meats)
          try {
            // 1. Send confirmation email to Customer
            try {
              const customerEmailPayload = {
                id: newOrderId,
                mediaURL: API_CONFIG.IMAGE_URL,
                subject: 'New Order Created',
                email: userInfo.email,
                username: `${userInfo.first_name} ${userInfo.last_name}`,
                store_phone: '1234567890',
                store_email: 'store@tazatropics.com'
              };

              console.log('Sending customer order email payload:', customerEmailPayload);
              const emailResult = await sendCustomerOrderEmail(customerEmailPayload, token);

              if (emailResult.success) {
                console.log('Customer order email sent successfully:', emailResult);
              } else {
                console.error('Failed to send customer order email:', emailResult.error, emailResult);
              }
            } catch (custEmailErr) {
              console.error('Error sending customer order email:', custEmailErr);
            }

            // 2. Send confirmation email copy to Store (store@tazatropics.com) directly via Brevo
            try {
              const storeEmailPayload = {
                orderId: newOrderId,
                recipientName: 'Store Admin',
                recipientEmail: 'store@tazatropics.com',
                items: cart.cart,
                total: cart.totalPrice,
                grandTotal: cart.grandTotal,
                tax: 0,
                deliveryCharge: cart.deliveryPrice,
                address: selectedAddress,
                paymentMethod: payMethodName || 'N/A',
                subject: ` New Order Received #${newOrderId}`
              };

              console.log('Sending store order email notification via Brevo:', storeEmailPayload);
              const storeEmailResult = await sendBrevoOrderEmail(storeEmailPayload);

              if (storeEmailResult.success) {
                console.log('Store order email copy sent via Brevo successfully:', storeEmailResult);
              } else {
                console.error('Failed to send store order email copy via Brevo:', storeEmailResult.error);
              }
            } catch (storeEmailErr) {
              console.error('Error sending store order email copy via Brevo:', storeEmailErr);
            }
          } catch (emailError) {
            console.error('Error in order email dispatch block:', emailError);
            // Don't fail the order if email fails
          }
        } catch (notifError) {
          // Error handling
          // Don't fail the order if notifications fail
        }
        
        // Navigate to orders page like Pure Meat does with success indicator
        navigate('/my-account?tab=orders&order_success=true');
      } else {
        showMessage('error', result.message || 'Failed to create order');
      }
    } catch (error) {
      // Error handling
      showMessage('error', 'Failed to create order. Please contact support.');
    } finally {
      orderCreationInFlightRef.current = false;
      setProcessing(false);
    }
  };
  
  const handleCODPayment = async () => {
    setProcessing(true);
    try {
      await createOrderAfterPayment('cod');
    } catch (error) {
      // Error handling
      showMessage('error', 'Failed to process COD payment');
      setProcessing(false);
    }
  };

  // PayPal Payment - Matches Pure Meat Implementation
  const handlePayPalPayment = async () => {
    setProcessing(true);
    
    try {
      // Get PayPal client ID from backend (same as Pure Meat)
      const keyResult = await getPayPalKey(token);
      
      if (!keyResult || !keyResult.success || !keyResult.key) {
        showMessage('error', keyResult?.message || 'PayPal configuration not available');
        setProcessing(false);
        return;
      }

      const clientId = keyResult.key;
      const totalAmount = calculateTotalAmount();
      
      // Auto-detect environment based on client ID
      // Sandbox keys start with "sb-" or "AZ" (sandbox pattern)
      // Live keys start with "A" followed by different pattern
      const isSandbox = clientId.startsWith('sb-') || clientId.startsWith('AZ');
      const environment = isSandbox ? 'sandbox' : 'live';
      
      // Load PayPal SDK with detected environment
      await loadPayPalSDK(clientId, currencyCode, environment);
      
      const paypal = window.paypal;
      if (!paypal) {
        showMessage('error', 'Failed to load PayPal SDK');
        setProcessing(false);
        return;
      }

      // Show PayPal modal (same as Pure Meat's payPalModal)
      showPayPalModal(paypal, totalAmount, currencyCode);

    } catch (error) {
      // Error handling
      showMessage('error', 'Failed to initialize PayPal');
      setProcessing(false);
    }
  };

  // Show PayPal Modal with Buttons (Pure Meat approach)
  const showPayPalModal = (paypal, totalAmount, currency) => {
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.id = 'paypal-modal-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.7);z-index:99999;display:flex;align-items:center;justify-content:center;';
    
    const modal = document.createElement('div');
    modal.style.cssText = 'background:white;padding:30px;border-radius:8px;max-width:450px;width:90%;box-shadow:0 4px 6px rgba(0,0,0,0.1);';
    
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;';
    
    const title = document.createElement('h3');
    title.textContent = 'Pay with PayPal';
    title.style.cssText = 'margin:0;color:#333;font-size:20px;';
    
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '×';
    closeBtn.style.cssText = 'background:transparent;border:none;font-size:28px;cursor:pointer;color:#999;padding:0;width:30px;height:30px;line-height:1;';
    closeBtn.onclick = () => {
      document.body.removeChild(overlay);
      setProcessing(false);
    };
    
    header.appendChild(title);
    header.appendChild(closeBtn);
    
    const buttonContainer = document.createElement('div');
    buttonContainer.id = 'paypal-button-container';
    buttonContainer.style.cssText = 'min-height:150px;';
    
    modal.appendChild(header);
    modal.appendChild(buttonContainer);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Configure PayPal Buttons (matches Pure Meat's initConfig)
    const paypalConfig = {
      style: {
        layout: 'vertical',
        label: 'paypal'
      },
      createOrder: (data, actions) => {
        // Same as Pure Meat's createOrderOnClient
        return actions.order.create({
          intent: 'CAPTURE',
          purchase_units: [{
            amount: {
              currency_code: currency,
              value: totalAmount.toFixed(2),
              breakdown: {
                item_total: {
                  currency_code: currency,
                  value: totalAmount.toFixed(2)
                }
              }
            }
          }]
        });
      },
      onApprove: async (data, actions) => {
        // Capture the payment and create order
        try {
          // Capture the payment
          const captureData = await actions.order.capture();
          
          // Close modal
          try {
            if (document.getElementById('paypal-modal-overlay')) {
              document.body.removeChild(overlay);
            }
          } catch (e) {
            // Error handling
          }
          
          // Set processing state
          setProcessing(true);
          
          try {
            // Create order in database with payment details
            await createOrderAfterPayment(JSON.stringify(captureData));
          } catch (error) {
            // Error handling
            showMessage('error', error.message || 'Failed to create order');
            setProcessing(false);
          }
        } catch (captureError) {
          // Error handling
          
          // Close modal on error
          try {
            if (document.getElementById('paypal-modal-overlay')) {
              document.body.removeChild(overlay);
            }
          } catch (e) {
            // Error handling
          }
          
          showMessage('error', 'Failed to capture payment. Please try again.');
          setProcessing(false);
        }
      },
      onCancel: (data, actions) => {
        document.body.removeChild(overlay);
        showMessage('info', 'Payment cancelled');
        setProcessing(false);
      },
      onError: (err) => {
        document.body.removeChild(overlay);
        showMessage('error', 'Payment failed');
        setProcessing(false);
      },
      onClick: (data, actions) => {
        // Click handling
      }
    };

    // Render PayPal Buttons
    paypal.Buttons(paypalConfig).render('#paypal-button-container').then(() => {
      setProcessing(false);
    }).catch(err => {
      // Error handling
      document.body.removeChild(overlay);
      showMessage('error', 'Failed to initialize PayPal');
      setProcessing(false);
    });
  };

  const loadPayPalSDK = (clientId, currency, environment = 'live') => {
    return new Promise((resolve, reject) => {
      // Remove any existing PayPal script to prevent conflicts
      const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
      if (existingScript) {
        existingScript.remove();
        // Also clear the window.paypal object to force fresh load
        delete window.paypal;
      }

      // Check if PayPal SDK already loaded with same environment
      if (window.paypal && window.__paypal_environment === environment) {
        resolve(window.paypal);
        return;
      }
      
      const script = document.createElement('script');
      // The PayPal SDK automatically detects sandbox vs live based on the client ID
      // But we can add debug parameter for sandbox
      const debugParam = environment === 'sandbox' ? '&debug=true' : '';
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}&intent=capture&disable-funding=credit,card${debugParam}`;
      script.async = true;
      script.onload = () => {
        window.__paypal_environment = environment; // Store environment for future checks
        resolve(window.paypal);
      };
      script.onerror = () => {
        // Error handling
        reject(new Error('Failed to load PayPal SDK'));
      };
      document.body.appendChild(script);
    });
  };



  // Success modal handlers
  const handleViewOrders = () => {
    setShowSuccessModal(false);
    navigate('/my-account?tab=orders');
  };

  const handleContinueShopping = () => {
    setShowSuccessModal(false);
    navigate('/');
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate('/my-account');
  };

  return (
    <>
      <Header />
      
      {/* Processing Overlay */}
      {processing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9997
        }}>
          <div className="spinner-border text-light" style={{ width: '4rem', height: '4rem' }} role="status">
            <span className="visually-hidden">Processing...</span>
          </div>
          <p className="text-white mt-3" style={{ fontSize: '18px', fontWeight: 500 }}>
            Processing your order...
          </p>
          <p className="text-white" style={{ fontSize: '14px', opacity: 0.8 }}>
            Please do not close this window
          </p>
        </div>
      )}
      
      <main className="main__content_wrapper" style={{background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)', minHeight: '60vh'}}>
        <div className="breadcrumb__section" style={{background: 'url(/assets/img/banner/breadcrumb-bg.jpg) center/cover no-repeat', padding: '48px 0 32px 0'}}>
          <div className="container">
            <h1 className="breadcrumb__title text-center" style={{color: '#fff', fontSize: '2.5rem', fontWeight: 700, textShadow: '0 2px 8px rgba(0,0,0,0.15)'}}>Checkout</h1>
          </div>
        </div>
        {/* Stepper */}
        <div className="container mt-4 mb-2">
          <div className="checkout-stepper d-flex justify-content-center align-items-center gap-4" style={{maxWidth: 600, margin: '0 auto'}}>
            {["Information", "Shipping", "Payment"].map((step, idx) => (
              <div key={step} className={`stepper-step d-flex flex-column align-items-center ${currentStep === idx+1 ? 'active' : ''}`} style={{flex: 1}}>
                <div className={`stepper-circle mb-1 ${currentStep >= idx+1 ? 'bg-primary text-white' : 'bg-light text-muted'}`} style={{width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18}}>{idx+1}</div>
                <span style={{fontSize: 14, fontWeight: currentStep === idx+1 ? 600 : 400}}>{step}</span>
              </div>
              ))}
          </div>
        </div>
        <section className="checkout__page--area">
          <div className="container py-5">
            <div className="checkout__page--inner d-flex flex-wrap gap-4 justify-content-center">
              <div className="main checkout__mian card shadow-lg rounded-4 border-0 p-4" style={{minWidth: '340px', maxWidth: '700px', flex: '1 1 700px', background: '#fff'}}>
                <header className="main__header checkout__mian--header mb-30">
                  <a className="logo logo__left mb-20" href="/">
                    <img src="/assets/img/logo/nav-log.png" alt="logo" />
                  </a>
                </header>
                <main className="main__content_wrapper section--padding pt-0">
                  <form onSubmit={handleSubmit}>
                    {/* Delivery Options Section */}
                    <div className="checkout__content--step section__shipping--address mb-4">
                      <div className="section__header mb-25">
                        <h2 className="section__header--title h3">Delivery Options</h2>
                        <p className="section__header--desc">Choose your preferred delivery method.</p>
                      </div>
                      <div className="checkout__content--step__inner3 border-radius-5 p-3" style={{background: '#f6f8fa'}}>
                        {/* Delivery Type Selection with icons */}
                        <div className="mb-20 d-flex gap-4">
                          <div className="shipping__contact--box__list flex-grow-1">
                            <div className="shipping__radio--input d-flex align-items-center gap-2">
                              <input
                                className="shipping__radio--input__field"
                                id="delivery-home"
                                name="deliveryType"
                                type="radio"
                                value="home"
                                checked={deliveryOption === 'home'}
                                onChange={(e) => setDeliveryOption(e.target.value)}
                              />
                              <span style={{fontSize: 22}} aria-label="Home"><FaHome color="#27ae60" /></span>
                              <label className="shipping__radio--label ms-2" htmlFor="delivery-home">
                                <span className="shipping__radio--label__primary">Home Delivery</span>
                                <span className="shipping__radio--label__desc">Deliver to your address</span>
                              </label>
                            </div>
                          </div>
                          <div className="shipping__contact--box__list flex-grow-1">
                            <div className="shipping__radio--input d-flex align-items-center gap-2">
                              <input
                                className="shipping__radio--input__field"
                                id="delivery-store"
                                name="deliveryType"
                                type="radio"
                                value="store"
                                checked={deliveryOption === 'store'}
                                onChange={(e) => setDeliveryOption(e.target.value)}
                              />
                              <span style={{fontSize: 22}} aria-label="Store"><FaStore color="#2980b9" /></span>
                              <label className="shipping__radio--label ms-2" htmlFor="delivery-store">
                                <span className="shipping__radio--label__primary">Store Pickup</span>
                                <span className="shipping__radio--label__desc">Collect from store</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Address Selection - Show only for Home Delivery */}
                    {(deliveryOption === 'home') && (
                      <div className="checkout__content--step section__shipping--address mb-4">
                        <div className="section__header mb-25">
                          <h2 className="section__header--title h3 d-flex align-items-center gap-2">
                            <FaHome color="#27ae60" style={{fontSize: 24}} />
                            Delivery Address
                          </h2>
                          <p className="section__header--desc">Select the address where you want your order delivered.</p>
                        </div>
                        {loadingAddresses ? (
                          <div className="checkout__content--step__inner3 border-radius-5 p-4 text-center">
                            <p>Loading addresses...</p>
                          </div>
                        ) : addresses.length > 0 ? (
                          <div className="checkout__content--step__inner3 border-radius-5 p-3" style={{background: '#f6f8fa'}}>
                            {addresses.map((addr) => (
                              <div key={addr.id} className="shipping__contact--box__list d-flex align-items-center gap-2 mb-2">
                                <input
                                  className="shipping__radio--input__field"
                                  id={`address-${addr.id}`}
                                  name="deliveryAddress"
                                  type="radio"
                                  checked={selectedAddressId === addr.id}
                                  onChange={() => setSelectedAddressId(addr.id)}
                                />
                                <span style={{fontSize: 20}} aria-label="Address"><FaHome color="#27ae60" /></span>
                                <label className="shipping__radio--label ms-2" htmlFor={`address-${addr.id}`}>
                                  <span className="shipping__radio--label__primary">{addr.title}</span>
                                  <span className="shipping__radio--label__desc">
                                    {addr.house && !addr.address?.toLowerCase().includes(addr.house.toLowerCase()) && `${addr.house}, `}
                                    {addr.landmark && !addr.address?.toLowerCase().includes(addr.landmark.toLowerCase()) && `${addr.landmark}, `}
                                    {addr.address}
                                    {addr.pincode && !addr.address?.toLowerCase().includes(addr.pincode.toLowerCase()) && ` - ${addr.pincode}`}
                                  </span>
                                </label>
                              </div>
                            ))}
                            <div className="checkout__content--step__footer mt-3">
                              <Link to="/my-account" className="checkout__continue__btn btn">
                                Add New Address
                              </Link>
                            </div>
                          </div>
                        ) : (
                          <div className="checkout__content--step__inner3 border-radius-5 p-4">
                            <p className="text-danger mb-3">No addresses found. Please add a delivery address.</p>
                            <Link to="/my-account" className="checkout__continue__btn btn">
                              Add Address
                            </Link>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Wallet Section */}
                    {walletBalance > 0 && (
                      <div className="checkout__content--step section__shipping--address mb-4">
                        <div className="section__header mb-25">
                          <h2 className="section__header--title h3 d-flex align-items-center gap-2">
                            <FaWallet color="#27ae60" style={{fontSize: 24}} />
                            Wallet Balance
                          </h2>
                          <p className="section__header--desc">Available balance: ${walletBalance.toFixed(2)}</p>
                        </div>
                        <div className="checkout__content--step__inner3 border-radius-5 p-3" style={{background: '#f6f8fa'}}>
                          <div className="shipping__contact--box__list d-flex align-items-center gap-2">
                            <input
                              className="shipping__radio--input__field"
                              id="use-wallet"
                              name="useWallet"
                              type="checkbox"
                              checked={useWalletBalance}
                              onChange={handleWalletChange}
                              disabled={false}
                            />
                            <label className="shipping__radio--label ms-2" htmlFor="use-wallet">
                              <span className="shipping__radio--label__primary">Use wallet balance</span>
                              <span className="shipping__radio--label__desc">
                                Apply $${walletBalance.toFixed(2)} from your wallet
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Contact Review Only */}
                    {/* Contact Review - show for both Home and Store */}
                    <div className="checkout__content--step checkout__contact--information2 border-radius-5 mb-4 p-3" style={{background: '#f6f8fa'}}>
                      <div className="checkout__review d-flex justify-content-between align-items-center mb-2">
                        <div className="checkout__review--inner d-flex align-items-center gap-2">
                          <span style={{fontSize: 20}} aria-label="Contact"><FaUserAlt color="#6366f1" /></span>
                          <label className="checkout__review--label">Contact</label>
                          <span className="checkout__review--content ms-2">{userInfo.mobile ? userInfo.mobile : userInfo.email}</span>
                        </div>
                        <div className="checkout__review--link">
                          <button className="checkout__review--link__text" type="button" onClick={() => navigate('/my-account?editProfile=true')}>Change</button>
                        </div>
                      </div>
                    </div>
                    {/* Payment Section */}
                    <div className="checkout__content--step section__shipping--address mb-4">
                      <div className="section__header mb-25">
                        <h2 className="section__header--title h3">Payment</h2>
                        <p className="section__header--desc">All transactions are secure and encrypted.</p>
                      </div>
                      {/* Payment Methods List with icons */}
                      {loadingPayments ? (
                        <div className="checkout__content--step__inner3 border-radius-5 p-4 text-center">
                          <p>Loading payment methods...</p>
                        </div>
                      ) : paymentMethods.length > 0 ? (
                        <div className="checkout__content--step__inner3 border-radius-5 p-3" style={{background: '#f6f8fa'}}>
                          {paymentMethods.map((method) => (
                            <div key={method.id} className="shipping__contact--box__list d-flex align-items-center gap-2 mb-2">
                              <input
                                className="shipping__radio--input__field"
                                id={`payment-${method.id}`}
                                name="paymentMethod"
                                type="radio"
                                checked={selectedPaymentId === method.id}
                                onChange={() => handlePaymentSelection(method)}
                              />
                              <span style={{fontSize: 22}} aria-label="Payment">
                                {(() => {
                                  switch (method.id) {
                                    case 1: return <FaMoneyBillWave color="#27ae60" />; // COD
                                    case 2: return <FaCcStripe color="#635bff" />; // Stripe
                                    case 3: return <FaPaypal color="#003087" />; // PayPal
                                    case 4: return <FaCcVisa color="#1a1f71" />; // PayTM (Visa icon as alternative)
                                    case 5: return <FaCreditCard color="#0f4fa8" />; // RazorPay (generic card)
                                    case 6: return <FaCreditCard color="#2d3436" />; // InstaMOJO
                                    case 7: return <FaCreditCard color="#2d3436" />; // PayStack
                                    case 8: return <FaCreditCard color="#2d3436" />; // Flutterwave
                                    default: return <FaCreditCard color="#2d3436" />;
                                  }
                                })()}
                              </span>
                              <label className="shipping__radio--label ms-2" htmlFor={`payment-${method.id}`}> 
                                <span className="shipping__radio--label__primary">{method.name}</span>
                                <span className="shipping__radio--label__desc">Pay with {method.name}</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="checkout__content--step__inner3 border-radius-5 p-4">
                          <p className="text-danger">No payment methods available. Please contact support.</p>
                        </div>
                      )}
                    </div>
                    <div className="checkout__content--step__footer d-flex align-items-center gap-3">
                      <button 
                        className="continue__shipping--btn btn border-radius-5 px-4 py-2" 
                        type="button"
                        onClick={handleProceedToPay}
                        disabled={!selectedPaymentId || processing}
                      >
                        {processing ? 'Processing...' : 'Place Order'}
                      </button>
                      <Link className="previous__link--content" to="/cart">Return to cart</Link>
                    </div>
                  </form>
                </main>
                <footer className="main__footer main__footer--wrapper mt-4">
                  {/* Footer removed as requested */}
                </footer>
              </div>
              {/* Sticky Sidebar */}
              <aside className="checkout__sidebar sidebar" style={{position: 'sticky', top: 32, alignSelf: 'flex-start', minWidth: 320, maxWidth: 400, background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.07)', padding: 24}}>
                <div className="cart__table checkout__product--table mb-3">
                  <table className="cart__table--inner">
                    <tbody className="cart__table--body">
                      {cart.cart.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-center py-4">Your cart is empty</td>
                        </tr>
                      ) : (
                        cart.cart.map((item) => (
                          <tr key={item.id} className="cart__table--body__items">
                            <td className="cart__table--body__list">
                              <div className="cart__product d-flex align-items-center gap-2">
                                <div className="product__thumbnail border-radius-5">
                                  <Link to={`/product/${item.id}`}>
                                    <img className="border-radius-5" src={getItemImage(item)} alt={item.name} style={{width: 48, height: 48, objectFit: 'cover'}} />
                                  </Link>
                                  <span className="product__thumbnail--quantity">{item.quantiy}</span>
                                </div>
                                <div className="product__description">
                                  <h3 className="product__description--name h5 mb-0">
                                    <Link to={`/product/${item.id}`}>{item.name}</Link>
                                  </h3>
                                  {item.store_name && item.store_name !== 'Default Store' && (
                                    <span className="product__description--variant">STORE: {item.store_name}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="cart__table--body__list">
                              <span className="cart__price">${getItemTotal(item)}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="checkout__discount--code mb-3">
                  <form className="d-flex" action="#">
                    <label className="flex-grow-1">
                      <input
                        className="checkout__discount--code__input--field border-radius-5"
                        placeholder="Gift card or discount code"
                        type="text"
                        name="discountCode"
                        value={formData.discountCode}
                        onChange={handleChange}
                      />
                    </label>
                    <button className="checkout__discount--code__btn btn border-radius-5 ms-2" type="submit">Apply</button>
                  </form>
                </div>
                <div className="checkout__total">
                  <table className="checkout__total--table">
                    <tbody className="checkout__total--body">
                      <tr className="checkout__total--items">
                        <td className="checkout__total--title text-left">Subtotal</td>
                        <td className="checkout__total--amount text-right">${subtotal.toFixed(2)}</td>
                      </tr>
                      {discount > 0 && (
                        <tr className="checkout__total--items">
                          <td className="checkout__total--title text-left">Discount</td>
                          <td className="checkout__total--amount text-right">-${discount.toFixed(2)}</td>
                        </tr>
                      )}
                      {/* {deliveryCharge > 0 && (
                        <tr className="checkout__total--items">
                          <td className="checkout__total--title text-left">Delivery</td>
                          <td className="checkout__total--amount text-right">${deliveryCharge.toFixed(2)}</td>
                        </tr>
                      )} */}
                      {/* <tr className="checkout__total--items">
                        <td className="checkout__total--title text-left">Tax</td>
                        <td className="checkout__total--amount text-right">${tax.toFixed(2)}</td>
                      </tr> */}
                    </tbody>
                    <tfoot className="checkout__total--footer">
                      <tr className="checkout__total--footer__items">
                        <td className="checkout__total--footer__title checkout__total--footer__list text-left">Total</td>
                        <td className="checkout__total--footer__amount checkout__total--footer__list text-right">${total.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Success Modal */}
      <OrderSuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        orderId={successOrderId}
        onViewOrders={handleViewOrders}
        onContinueShopping={handleContinueShopping}
      />


    </>
  );
};

export default CheckoutPage;
